import { animated } from '@react-spring/web'
import { useCallback, useContext, useMemo } from 'react'

import { FrameBuilderSelectionContext } from '@frame-builder/contexts/selection-context'
import { FrameBuilderSelectedStateContext } from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import { useBlinkingState } from '@frame-builder/lib/preview-animation'
import { type StepDefinitionProps } from '@frame-builder/lib/steps'

import Button from '@frame-builder/components/utility/button'
import ColorInput from '@frame-builder/components/utility/color-input'
import PortableText from '@frame-builder/components/utility/portable-text'
import ConfigurationDefinition from './configuration-definition'

const ArtworkImageDefinition = ({
  stepNumber,
  onClose,
  className,
}: StepDefinitionProps) => {
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { selectedStateSelectionMessages } = useContext(
    FrameBuilderSelectionContext,
  )
  const { validationErrorMessages } = useContext(FrameBuilderValidationContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)

  const [opacity, setIsBlinking] = useBlinkingState()

  const isValidSelection = useMemo(() => {
    return !validationErrorMessages?.artworkImage
  }, [validationErrorMessages])

  const updateImage = useCallback(
    (url: string, name: string) => {
      dispatchSelectedState({
        type: 'artworkImage',
        url,
        name,
      })
    },
    [dispatchSelectedState],
  )

  const updateBackgroundColor = useCallback(
    (color: string) => {
      dispatchSelectedState({
        type: 'artworkImage',
        color,
      })
    },
    [dispatchSelectedState],
  )

  const updateFile = useCallback(
    (files: FileList | null) => {
      let file: File | null = null
      const fileCount = files?.length ?? 0

      for (let i = 0; i < fileCount; i += 1) {
        if (files?.[i].type.match(/^image\//)) {
          file = files[i]
          break
        }
      }

      if (file !== null) {
        const fileUrl = URL.createObjectURL(file)
        updateImage(fileUrl, file.name)
      }
    },
    [updateImage],
  )

  const handleOkClick = useCallback(() => {
    if (isValidSelection) {
      onClose()
    }

    if (!isValidSelection) {
      setIsBlinking(true)
    }
  }, [isValidSelection, onClose, setIsBlinking])

  return (
    <ConfigurationDefinition
      stepNumber={stepNumber}
      title={frameBuilderStrings.artworkPreviewStepTitle}
      footerContent={frameBuilderStrings.artworkPreviewStepFooter}
      onClose={onClose}
      className={className}
    >
      <div className="flex flex-col px-5 gap-y-5">
        <animated.div
          className="text-sm space-y-3"
          style={{
            opacity,
          }}
        >
          <div className="space-y-1.5">
            {!!frameBuilderStrings.artworkPreviewArtworkLabel && (
              <span>{frameBuilderStrings.artworkPreviewArtworkLabel}</span>
            )}
            {!!frameBuilderStrings.artworkPreviewArtworkDescription && (
              <PortableText
                className="[&>*]:text-xs"
                content={frameBuilderStrings.artworkPreviewArtworkDescription}
              />
            )}
          </div>

          <label className="flex items-center gap-x-3">
            <span className="inline-flex whitespace-nowrap text-sm px-4 py-1.5 border border-input-border hover:border-input-border/60 transition-colors rounded-full cursor-pointer">
              {frameBuilderStrings.chooseFileLabel}
            </span>
            <span>{selectedStateSelectionMessages?.artworkImage}</span>
            <input
              type="file"
              accept="image/*"
              onChange={(event) => updateFile(event.target.files)}
              className="hidden"
            />
          </label>

          {!!validationErrorMessages.artworkImage && (
            <div className="px-3 py-2.5 rounded bg-error/10 text-sm">
              <span>{validationErrorMessages.artworkImage}</span>
            </div>
          )}
        </animated.div>

        <div className="flex flex-col text-sm gap-y-3">
          {!!frameBuilderStrings.artworkPreviewBackgroundColorLabel && (
            <span>
              {frameBuilderStrings.artworkPreviewBackgroundColorLabel}
            </span>
          )}

          <ColorInput
            defaultValue={
              selectedState.artwork.backgroundColor?.current ?? '#ffffff'
            }
            placeholder={
              frameBuilderStrings.artworkPreviewBackgroundColorPlaceholder
            }
            predefinedColors={
              selectedState.artwork.backgroundColor?.predefinedColors ?? []
            }
            onChange={updateBackgroundColor}
          />
        </div>

        <div>
          <Button
            size="small"
            isVisuallyDisabled={!isValidSelection}
            onClick={handleOkClick}
            className="min-w-32 grow-0 shrink-0"
          >
            {frameBuilderStrings.controlOkLabel}
          </Button>
        </div>
      </div>
    </ConfigurationDefinition>
  )
}

export default ArtworkImageDefinition
